/**
 * Unified Weight Calculation Utility
 *
 * Single source of truth for how effective weight is determined for any exercise type.
 *
 * Exercise types and how weight is stored in workout_logs.weight:
 *   - Regular resistance: raw weight lifted (kg)
 *   - Bodyweight (is_bodyweight=true): WorkoutLogger stores 0 (or added vest/belt weight); effective weight is recalculated at read time as BW × multiplier
 *   - Assisted (is_assisted=true): stores the assistance amount (e.g. 20kg of band assist)
 *
 * Effective weight for analytics/volume calculations:
 *   - Regular: log.weight as-is
 *   - Bodyweight: userBodyweight × bodyweight_multiplier  (recalculates from current BW)
 *   - Assisted: max(0, userBodyweight − log.weight)       (BW minus assistance)
 *
 * Note: workoutSummaryService uses log.weight directly for bodyweight exercises because
 * it wants the weight that was actually recorded at session time, not recalculated from
 * current bodyweight. All analytics / chart code should use getEffectiveWeight instead.
 */

/**
 * Calculate effective weight for a logged set.
 *
 * @param {number|string} rawWeight  - Value from workout_logs.weight
 * @param {Object}        exercise   - Exercise row: { is_bodyweight, is_assisted, bodyweight_multiplier }
 * @param {number}        userBodyweight - User's current bodyweight (kg), from users.bodyweight_kg
 * @returns {number} Effective weight in kg (never negative)
 */
export const getEffectiveWeight = (rawWeight, exercise, userBodyweight = 0) => {
  const w = parseFloat(rawWeight) || 0;

  if (!exercise) return w;

  if (exercise.is_bodyweight && userBodyweight > 0) {
    return userBodyweight * (parseFloat(exercise.bodyweight_multiplier) || 1.0);
  }

  if (exercise.is_assisted && userBodyweight > 0) {
    return Math.max(0, userBodyweight - w);
  }

  return w;
};

/**
 * Calculate set volume (effective weight × reps).
 *
 * @param {number|string} rawWeight    - Value from workout_logs.weight
 * @param {number|string} reps         - Value from workout_logs.reps
 * @param {Object}        exercise     - Exercise row
 * @param {number}        userBodyweight - User's current bodyweight (kg)
 * @returns {number} Volume in kg·reps
 */
export const getSetVolume = (rawWeight, reps, exercise, userBodyweight = 0) =>
  getEffectiveWeight(rawWeight, exercise, userBodyweight) * (parseInt(reps) || 0);
